import React, { useCallback } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import type { MarkdownModeView } from '@atlassian/editor-plugin-markdown-mode';
import { MarkdownModeViewToggleBase } from '@atlassian/editor-plugin-markdown-mode/ui-components';

import { useIsMarkdownMode } from '@confluence/content-markdown-mode/entry-points/useIsMarkdownMode.ts';

import { useMarkdownModeView } from './markdown-mode-view-store.ts';

export type MarkdownModeViewToggleProps = {
	contentId: string | undefined;
};

const MARKDOWN_MODE_VIEW_TOGGLE_ACTION_SUBJECT_ID = 'markdownModeViewToggle';

/**
 * Confluence-specific wrapper around the shared `MarkdownModeViewToggleBase`
 * presentational component.
 *
 * This wrapper adds:
 * - The `useIsMarkdownMode(contentId)` visibility gate (GraphQL + experiment)
 * - Confluence analytics (`sendUIEvent`) on view changes
 * - Sweet-state read/write via `useMarkdownModeView()`
 *
 * Renders nothing on non-Markdown-Mode pages.
 *
 * The component does **not** mount its own `MarkdownModeViewContainer` — the
 * container is mounted once at the live-doc editor root so the toggle and the
 * editor body share one `contentId`-scoped state.
 */
export const MarkdownModeViewToggle = ({ contentId }: MarkdownModeViewToggleProps) => {
	const isMarkdown = useIsMarkdownMode(contentId);
	const [{ view }, { setView }] = useMarkdownModeView();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleViewChange = useCallback(
		(nextView: MarkdownModeView) => {
			// Confluence's analytics listener requires the wrapped `{ type, data }`
			// shape — without `type: 'sendUIEvent'` the listener throws
			// "Received Unknown Type for Analytics Web Client: undefined" at runtime.
			// `.fire()` returns a Promise we don't await — analytics dispatch is
			// fire-and-forget and we don't want to block the view change on it.
			// Mark it explicitly with `void` to satisfy `no-floating-promises`
			// while still surfacing the intent to readers.
			void createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: MARKDOWN_MODE_VIEW_TOGGLE_ACTION_SUBJECT_ID,
					source: 'markdownModeViewToggle',
					attributes: {
						from: view,
						to: nextView,
						contentId,
					},
				},
			}).fire();

			// `setView` from react-sweet-state returns a Promise we deliberately
			// don't await — the state update is synchronous from the user's
			// point of view; `void` satisfies `no-floating-promises`.
			void setView(nextView);
		},
		[view, setView, createAnalyticsEvent, contentId],
	);

	if (!isMarkdown) {
		return null;
	}

	return <MarkdownModeViewToggleBase view={view} onViewChange={handleViewChange} />;
};
